<template>
  <div>
    <!-- ── Branch membership (multi-branch businesses only) ── -->
    <template v-if="multi">
      <div class="text-[11px] font-semibold text-gray-500 mx-0.5 mb-2">סניפים שבהם עובד/ת {{ firstName }}</div>

      <!-- Not yet assigned to any branch -->
      <div v-if="joined.length === 0" class="border border-dashed border-gray-300 rounded-xl p-4 text-center mb-2">
        <template v-if="canManage">
          <div class="text-[12.5px] text-gray-600 mb-3">עדיין לא שויך/ה לסניף. בחרו סניף כדי לקבוע בו שעות.</div>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary text-white text-[13px] font-medium"
            @click="openJoinSheet"
          >
            <i class="pi pi-plus text-sm" /> שיוך לסניף
          </button>
        </template>
        <div v-else class="text-[12.5px] text-gray-600">עדיין לא שויכת לסניף. פנו למנהל/ת העסק כדי שישייך אתכם לסניף, ואז תוכלו לקבוע בו שעות.</div>
      </div>

      <!-- Branch tabs -->
      <template v-else>
        <div class="flex gap-1.5 overflow-x-auto pb-1 mb-1.5">
          <button
            v-for="b in joined"
            :key="b.id"
            type="button"
            class="flex-none min-w-[118px] px-3 py-2 rounded-xl border text-right transition-colors"
            :class="b.id === sel ? 'border-primary bg-gradient-to-br from-primary-50 to-[#f0eefa]' : 'border-gray-200 bg-white'"
            @click="sel = b.id"
          >
            <div class="flex items-center gap-1.5">
              <i class="pi pi-map-marker text-[10px]" :class="b.id === sel ? 'text-primary' : 'text-gray-400'" />
              <span class="text-[12.5px] font-semibold truncate max-w-[104px]" :class="b.id === sel ? 'text-primary-700' : 'text-gray-800'">{{ b.name }}</span>
            </div>
            <div class="text-[10.5px] mt-0.5" :class="b.id === sel ? 'text-primary' : 'text-gray-500'">{{ fmtHours(totalFor(b.id)) }} שע׳/שבוע</div>
          </button>
          <button
            v-if="remaining.length && canManage"
            type="button"
            class="flex-none flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-dashed border-gray-300 text-gray-500 text-[12.5px] whitespace-nowrap"
            @click="openJoinSheet"
          >
            <i class="pi pi-plus text-xs" /> סניף
          </button>
        </div>
        <div class="text-[10.5px] text-gray-400 mx-0.5 mb-3.5">לכל סניף לוח שעות משלו — הקישו סניף כדי לערוך את שעותיו</div>
      </template>
    </template>

    <!-- ── Single-branch header ── -->
    <div v-else class="flex items-center gap-2 mb-3.5 px-2.5 py-2 bg-primary-50 rounded-lg">
      <i class="pi pi-sparkles text-primary text-xs flex-none" />
      <span class="text-[11px] text-primary-700">שעות העבודה השבועיות</span>
    </div>

    <!-- ── Weekly editor (scoped to the selected branch in multi mode) ── -->
    <div v-if="showEditor">
      <div v-if="multi" class="flex items-center justify-between mb-3">
        <div class="flex items-baseline gap-1.5 min-w-0">
          <span class="text-[13.5px] font-semibold truncate">{{ branchName(sel) }}</span>
          <span class="text-[11px] text-gray-500 whitespace-nowrap">· {{ fmtHours(totalFor(sel)) }} שע׳/שבוע</span>
        </div>
        <button v-if="canManage" type="button" class="text-[11.5px] text-red-600 flex-none ms-2" @click="leaveBranch(sel)">הסרה מהסניף</button>
      </div>

      <div
        v-for="(label, di) in DAY_LABELS"
        :key="di"
        class="pb-3.5 mb-3.5"
        :class="di < DAY_LABELS.length - 1 ? 'border-b border-gray-100' : ''"
      >
        <div class="flex items-center justify-between" :class="daySlots(di).length ? 'mb-2.5' : 'mb-1.5'">
          <div class="flex items-center gap-2.5">
            <span class="text-[15px] font-semibold">{{ label }}</span>
            <button
              v-if="daySlots(di).length && di <= 4"
              type="button"
              class="flex items-center gap-1 text-[10.5px] text-primary-600"
              @click="copyToWeekdays(di)"
            >
              <i class="pi pi-copy text-[11px]" /> לכל ימי החול
            </button>
          </div>
          <button
            type="button"
            class="w-[30px] h-[30px] rounded-lg border border-gray-200 bg-white flex items-center justify-center"
            @click="addSlot(di)"
          >
            <i class="pi pi-plus text-primary text-sm" />
          </button>
        </div>

        <div v-if="!daySlots(di).length" class="text-[12.5px] text-gray-400">סגור</div>
        <div v-else class="flex flex-col gap-2.5">
          <div v-for="(slot, si) in daySlots(di)" :key="si" class="flex items-center gap-2">
            <TimeField :model-value="hourStr(slot.from)" :options="TIMES" @update:model-value="v => (slot.from = v)" />
            <div class="w-3.5 h-px bg-gray-300 flex-none" />
            <TimeField :model-value="hourStr(slot.to)" :options="TIMES" @update:model-value="v => (slot.to = v)" />
            <button type="button" class="w-[30px] h-[30px] flex items-center justify-center flex-none" @click="delSlot(di, slot)">
              <i class="pi pi-trash text-gray-400 text-sm" />
            </button>
          </div>
          <div v-if="conflictOn(di)" class="flex items-center gap-1.5 text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
            <i class="pi pi-exclamation-triangle text-[11px]" /> חופף לשעות בסניף אחר באותו יום
          </div>
        </div>
      </div>
    </div>

    <!-- ── Extra hours for a date range ──
         One-off additions on top of the weekly schedule (holiday evenings, a busy
         season). They add availability without touching the weekly grid. -->
    <div class="pt-1">
      <div class="flex items-center justify-between gap-3 mb-2.5">
        <div class="flex flex-col min-w-0">
          <span class="text-[13.5px] font-semibold">שעות נוספות לתאריכים מיוחדים</span>
          <span class="text-[10.5px] text-gray-500">תוספת שעות לטווח תאריכים, מעבר ללוח השבועי</span>
        </div>
        <button
          v-if="canManage"
          type="button"
          class="flex-none flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-[12.5px] text-gray-700"
          @click="openCreateOverride"
        >
          <i class="pi pi-plus text-primary text-xs" /> הוספה
        </button>
      </div>

      <div v-if="!overrides.length" class="border border-dashed border-gray-300 rounded-xl px-4 py-5 text-center text-[12.5px] text-gray-500">
        אין שעות נוספות מוגדרות
      </div>
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="override in overrides"
          :key="override._id"
          class="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-2.5"
        >
          <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none">
            <i class="pi pi-calendar-plus text-sm" />
          </span>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <div class="flex items-center gap-1.5 flex-wrap">
              <span v-if="override.label" class="text-[13px] font-medium truncate">{{ override.label }}</span>
              <span v-else class="text-[13px] font-medium">שעות נוספות</span>
              <span
                v-if="multi"
                class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 flex items-center gap-1"
              >
                <i class="pi pi-map-marker text-[9px]" />{{ overrideBranchName(override.branchId) }}
              </span>
            </div>
            <span class="text-[12px] text-gray-600">{{ formatDateRange(override.startDate, override.endDate) }}</span>
            <span v-if="override.daysOfWeek?.length" class="text-[11px] text-gray-500">
              בימים: {{ override.daysOfWeek.map(d => weekDaysInHebrew[d]).join(', ') }}
            </span>
            <span class="text-[12px] text-gray-800" style="text-align: start">
              {{ override.hours.map(h => `${hourStr(h.from)}–${hourStr(h.to)}`).join(', ') }}
            </span>
          </div>
          <div v-if="canManage" class="flex gap-0.5 flex-none">
            <button type="button" class="w-8 h-8 flex items-center justify-center" @click="openEditOverride(override)">
              <i class="pi pi-pencil text-gray-400 text-sm" />
            </button>
            <button type="button" class="w-8 h-8 flex items-center justify-center" @click="confirmDeleteOverride(override)">
              <i class="pi pi-trash text-red-400 text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { IAvailability, Days as DaysType, IAvailabilityOverride, IWorkingHoursItem } from '@torim-app/types';
import { Days, MAIN_BRANCH_ID, weekDaysInHebrew } from '@torim-app/types';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import { useDrawer } from '@torim-app/shared-components';
import { useAdminAvailabilityStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';
import AvailabilityOverrideEditor from '@app/components/admin/modals/AvailabilityOverrideEditor.vue';
import TimeField from './TimeField.vue';
import JoinBranchSheet from './JoinBranchSheet.vue';

const props = withDefaults(defineProps<{
  workingHours: IAvailability;
  branches: Array<{ id: string; name: string; address?: string }>;
  // Whose schedule this is — the extra-hours entries are stored per provider.
  providerId: string;
  memberName?: string;
  // Employees edit their hours in the branches they were assigned to, but joining or
  // leaving a branch stays a manager decision.
  canManage?: boolean;
}>(), { canManage: true });

const { openDrawer, closeDrawer } = useDrawer();
const adminAvailabilityStore = useAdminAvailabilityStore();
const toast = useToast();
const confirm = useConfirm();

const DAY_ORDER: DaysType[] = [Days.SUNDAY, Days.MONDAY, Days.TUESDAY, Days.WEDNESDAY, Days.THURSDAY, Days.FRIDAY, Days.SATURDAY];
const DAY_LABELS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
// Time options: 06:00–22:00 at :00 / :30.
const TIMES: string[] = (() => {
  const a: string[] = [];
  for (let h = 6; h <= 22; h++) { a.push(String(h).padStart(2, '0') + ':00'); if (h < 22) a.push(String(h).padStart(2, '0') + ':30'); }
  return a;
})();

const multi = computed(() => props.branches.length > 1);
const firstName = computed(() => (props.memberName ?? '').trim().split(' ')[0]);

const hourStr = (h: IWorkingHoursItem['from']): string => (typeof h === 'string' ? h : h?.name);
const toH = (t: string): number => { const p = String(t).split(':'); return (+p[0] || 0) + (+p[1] || 0) / 60; };
const fmtHours = (h: number): string => (Number.isInteger(h) ? String(h) : h.toFixed(1));

const dayArr = (di: number): IWorkingHoursItem[] => props.workingHours[DAY_ORDER[di]] ?? [];
const branchOf = (s: IWorkingHoursItem): string => (s.branchId || MAIN_BRANCH_ID);
const setDay = (di: number, arr: IWorkingHoursItem[]) => { props.workingHours[DAY_ORDER[di]] = arr; };

// Membership is derived: a branch is "joined" if any slot references it.
const joined = computed(() => props.branches.filter(b => DAY_ORDER.some((_, di) => dayArr(di).some(s => branchOf(s) === b.id))));
const remaining = computed(() => props.branches.filter(b => !joined.value.some(j => j.id === b.id)));

const branchName = (bid: string) => props.branches.find(b => b.id === bid)?.name ?? '';
const totalFor = (bid: string): number => {
  let t = 0;
  for (let di = 0; di < 7; di++) for (const s of dayArr(di)) if (branchOf(s) === bid) t += Math.max(0, toH(hourStr(s.to)) - toH(hourStr(s.from)));
  return t;
};

// Selected branch tab (multi mode only). Keep it valid as membership changes.
const sel = ref<string>('');
const ensureSel = () => {
  if (!multi.value) { sel.value = ''; return; }
  if (joined.value.some(b => b.id === sel.value)) return;
  sel.value = joined.value[0]?.id ?? props.branches[0]?.id ?? '';
};
ensureSel();
watch([joined, multi], ensureSel);

// The editor is scoped to the selected branch in multi mode, or all slots when single-branch.
const daySlots = (di: number): IWorkingHoursItem[] =>
  multi.value ? dayArr(di).filter(s => branchOf(s) === sel.value) : dayArr(di);
const showEditor = computed(() => !multi.value || joined.value.length > 0);

const newSlot = (): IWorkingHoursItem => {
  const branchId = multi.value ? sel.value : undefined;
  return { from: '09:00', to: '18:00', ...(branchId ? { branchId } : {}) };
};
const addSlot = (di: number) => setDay(di, [...dayArr(di), newSlot()]);
const delSlot = (di: number, slot: IWorkingHoursItem) => setDay(di, dayArr(di).filter(s => s !== slot));

const copyToWeekdays = (di: number) => {
  const src = daySlots(di).map(s => ({ from: hourStr(s.from), to: hourStr(s.to) }));
  const branchId = multi.value ? sel.value : undefined;
  for (let dd = 0; dd <= 4; dd++) {
    const others = multi.value ? dayArr(dd).filter(s => branchOf(s) !== sel.value) : [];
    setDay(dd, [...others, ...src.map(x => ({ from: x.from, to: x.to, ...(branchId ? { branchId } : {}) }))]);
  }
};

// Amber warning when the selected branch overlaps another joined branch the same day.
const conflictOn = (di: number): boolean => {
  if (!multi.value) return false;
  const mine = daySlots(di);
  return joined.value.some(b => b.id !== sel.value && dayArr(di).some(o => branchOf(o) === b.id &&
    mine.some(x => toH(hourStr(x.from)) < toH(hourStr(o.to)) && toH(hourStr(o.from)) < toH(hourStr(x.to)))));
};

const joinBranch = (bid: string) => {
  for (let dd = 0; dd <= 4; dd++) setDay(dd, [...dayArr(dd), { from: '09:00', to: '18:00', branchId: bid }]);
  sel.value = bid;
  closeDrawer();
};
const leaveBranch = (bid: string) => {
  for (let di = 0; di < 7; di++) setDay(di, dayArr(di).filter(s => branchOf(s) !== bid));
  if (sel.value === bid) sel.value = joined.value[0]?.id ?? props.branches[0]?.id ?? '';
};

const openJoinSheet = () => {
  openDrawer(JoinBranchSheet, { remaining: remaining.value, onPick: joinBranch, onClose: closeDrawer }, { title: 'שיוך לסניף' });
};

// ── Extra hours (date-range availability additions) ──
// These live in their own collection rather than the weekly grid, so they save
// immediately from their editor — independent of this tab's save bar.
const overrides = computed<IAvailabilityOverride[]>(() =>
  adminAvailabilityStore.getOverridesByProvider(props.providerId)
);

const overrideBranchName = (branchId?: string): string =>
  props.branches.find(b => b.id === (branchId || MAIN_BRANCH_ID))?.name ?? 'סניף ראשי';

const formatDateRange = (start: string, end: string): string => {
  const s = DateTime.fromISO(start).setLocale('he').toFormat('dd/LL/yyyy');
  const e = DateTime.fromISO(end).setLocale('he').toFormat('dd/LL/yyyy');
  return s === e ? s : `${s} – ${e}`;
};

const openCreateOverride = () => {
  trackEvent('availability_override_add_clicked');
  openDrawer(
    AvailabilityOverrideEditor,
    { providerId: props.providerId, mode: 'create', onClose: closeDrawer },
    { title: 'הוספת שעות נוספות' }
  );
};

const openEditOverride = (override: IAvailabilityOverride) => {
  trackEvent('availability_override_edit_clicked');
  openDrawer(
    AvailabilityOverrideEditor,
    { providerId: props.providerId, override, mode: 'edit', onClose: closeDrawer },
    { title: 'עריכת שעות נוספות' }
  );
};

const confirmDeleteOverride = (override: IAvailabilityOverride) => {
  confirm.require({
    message: 'למחוק את התוספת?',
    header: 'אישור מחיקה',
    rejectLabel: 'ביטול',
    acceptLabel: 'מחיקה',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await adminAvailabilityStore.deleteAvailabilityOverride(override._id!);
        toast.success('התוספת נמחקה');
      } catch (e) {
        console.error(e);
        toast.error('לא הצלחנו למחוק את התוספת');
      }
    },
  });
};
</script>
